import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Search,
  RefreshCw,
  Database,
  AlertCircle,
  CheckCircle,
  Settings,
  Table,
  Columns,
  FolderOpen,
  FileJson,
  Upload,
  Code
} from 'lucide-react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
// CHA-276 — Carbon pass (Modules concept). Presentation only: BIDataService,
// callables, schema validation, and CRUD flows are byte-identical.
import { SkeletonRows, NoResults } from './shell/carbonStates';

// Carbon class shorthands (chrome only)
const TH = 'px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border';
const CHIP = 'font-plex-mono text-[9.5px] tracking-[0.08em] uppercase font-medium border px-2 py-0.5';
const INPUT = 'w-full px-3 py-2 text-sm bg-carbon-white text-carbon-ink border focus:outline-none focus:ring-1 focus:ring-carbon-blue-60';
const BTN_PRIMARY = 'px-4 py-2 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50';
const BTN_OUTLINE = 'px-4 py-2 text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10 disabled:opacity-50';

// Service class for BI Data settings
class BIDataServiceClass {
  constructor() {
    this.functions = null;
    this.db = null;
    this.initialized = false;
    this.configTableName = 'bi_data_config';
    this.schemaTableName = 'bi_table_schemas';
  }

  async initialize() {
    if (this.initialized) return;
    try {
      let app;
      if (getApps().length === 0) {
        const firebaseConfig = typeof window.__firebase_config !== 'undefined' ? JSON.parse(window.__firebase_config) : {};
        app = initializeApp(firebaseConfig);
      } else {
        app = getApp();
      }
      this.functions = getFunctions();
      this.db = getFirestore(app);
      this.initialized = true;
      console.log('BIDataService initialized');
    } catch (error) {
      console.error('Failed to initialize BIDataService:', error);
      throw error;
    }
  }

  // Get BI configuration (bucket name, etc.)
  async getConfig() {
    await this.initialize();
    try {
      // Get BigQuery config
      const getLookupTableData = httpsCallable(this.functions, 'getLookupTableData');
      const result = await getLookupTableData({ tableName: this.configTableName });
      const data = result.data.data || [];
      const bqConfig = data[0] || { bucket_name: '', dataset_name: 'inmocean_data' };

      // Get tenant_path from Firestore
      const tenantConfigRef = doc(this.db, 'bi_tenant_config', 'inmocean');
      const tenantConfigSnap = await getDoc(tenantConfigRef);
      const tenantConfig = tenantConfigSnap.exists() ? tenantConfigSnap.data() : {};

      // Merge configs
      return {
        ...bqConfig,
        tenant_path: tenantConfig.tenant_path || ''
      };
    } catch (error) {
      console.error('Error getting BI config:', error);
      // Return default config if table doesn't exist
      return { bucket_name: '', dataset_name: 'inmocean_data', tenant_path: '' };
    }
  }

  // Save BI configuration
  async saveConfig(config) {
    await this.initialize();
    try {
      // Extract tenant_path for Firestore, keep rest for BigQuery
      const { tenant_path, ...bqConfig } = config;

      // Save tenant_path to Firestore
      if (tenant_path !== undefined) {
        const tenantConfigRef = doc(this.db, 'bi_tenant_config', 'inmocean');
        await setDoc(tenantConfigRef, {
          tenant_path,
          updated_at: new Date().toISOString()
        }, { merge: true });
        console.log('✅ Saved tenant_path to Firestore:', tenant_path);
      }

      // Save BigQuery config (without tenant_path)
      const existingConfig = await this.getConfig();
      if (existingConfig.id) {
        // Update existing
        const updateLookupRecord = httpsCallable(this.functions, 'updateLookupRecord');
        const result = await updateLookupRecord({
          tableName: this.configTableName,
          id: existingConfig.id,
          record: bqConfig
        });
        return { ...result.data.data.updatedRecord, tenant_path };
      } else {
        // Create new
        const addLookupRecord = httpsCallable(this.functions, 'addLookupRecord');
        const result = await addLookupRecord({ tableName: this.configTableName, record: bqConfig });
        return { ...result.data.data, tenant_path };
      }
    } catch (error) {
      console.error('Error saving BI config:', error);
      throw new Error(`Failed to save config: ${error.message}`);
    }
  }

  // Get all table schemas
  async getTableSchemas() {
    await this.initialize();
    try {
      const getLookupTableData = httpsCallable(this.functions, 'getLookupTableData');
      const result = await getLookupTableData({ tableName: this.schemaTableName });
      return result.data.data || [];
    } catch (error) {
      console.error('Error getting table schemas:', error);
      return [];
    }
  }

  // Add a new table schema
  async addTableSchema(schema) {
    await this.initialize();
    try {
      const addLookupRecord = httpsCallable(this.functions, 'addLookupRecord');
      const result = await addLookupRecord({ tableName: this.schemaTableName, record: schema });
      return result.data.data;
    } catch (error) {
      console.error('Error adding table schema:', error);
      throw new Error(`Failed to add schema: ${error.message}`);
    }
  }

  // Update a table schema
  async updateTableSchema(id, schema) {
    await this.initialize();
    try {
      const updateLookupRecord = httpsCallable(this.functions, 'updateLookupRecord');
      const result = await updateLookupRecord({ tableName: this.schemaTableName, id, record: schema });
      return result.data.data.updatedRecord;
    } catch (error) {
      console.error('Error updating table schema:', error);
      throw new Error(`Failed to update schema: ${error.message}`);
    }
  }

  // Delete a table schema
  async deleteTableSchema(id) {
    await this.initialize();
    try {
      const deleteLookupRecord = httpsCallable(this.functions, 'deleteLookupRecord');
      const result = await deleteLookupRecord({ tableName: this.schemaTableName, id });
      return result.data.data.success;
    } catch (error) {
      console.error('Error deleting table schema:', error);
      throw new Error(`Failed to delete schema: ${error.message}`);
    }
  }
}

export const BIDataService = new BIDataServiceClass();

// Helper function to extract column names from BigQuery JSON schema
function extractColumnsFromJsonSchema(jsonSchema) {
  try {
    const schema = typeof jsonSchema === 'string' ? JSON.parse(jsonSchema) : jsonSchema;
    if (!Array.isArray(schema)) {
      throw new Error('Schema must be an array of field definitions');
    }
    return schema.map(field => field.name);
  } catch (err) {
    throw new Error(`Invalid JSON schema: ${err.message}`);
  }
}

// Schema Form Component
function SchemaForm({ record, onSave, onCancel, isEditing }) {
  const fileInputRef = useRef(null);
  const [inputMode, setInputMode] = useState(record?.schema_json ? 'json' : 'simple');
  const [formData, setFormData] = useState(record || {
    table_name: '',
    display_name: '',
    columns: '',
    schema_json: '',
    bucket_path: '',
    description: '',
    active: true
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [jsonPreview, setJsonPreview] = useState({ valid: false, columns: [], error: null });

  // Parse JSON schema when it changes
  useEffect(() => {
    if (inputMode === 'json' && formData.schema_json) {
      try {
        const columns = extractColumnsFromJsonSchema(formData.schema_json);
        setJsonPreview({ valid: true, columns, error: null });
        // Auto-update columns field
        setFormData(prev => ({ ...prev, columns: columns.join(',') }));
      } catch (err) {
        setJsonPreview({ valid: false, columns: [], error: err.message });
      }
    } else if (inputMode === 'simple') {
      setJsonPreview({ valid: false, columns: [], error: null });
    }
  }, [formData.schema_json, inputMode]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.table_name?.trim()) {
      newErrors.table_name = 'Table name is required';
    } else if (!/^[a-z][a-z0-9_]*$/.test(formData.table_name)) {
      newErrors.table_name = 'Table name must start with a letter and contain only lowercase letters, numbers, and underscores';
    }
    if (!formData.display_name?.trim()) {
      newErrors.display_name = 'Display name is required';
    }

    if (inputMode === 'json') {
      if (!formData.schema_json?.trim()) {
        newErrors.schema_json = 'JSON schema is required';
      } else if (!jsonPreview.valid) {
        newErrors.schema_json = jsonPreview.error || 'Invalid JSON schema';
      }
    } else {
      if (!formData.columns?.trim()) {
        newErrors.columns = 'Columns are required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setSaving(true);
      try {
        // Prepare data - ensure columns are set from JSON if using JSON mode
        const dataToSave = { ...formData };
        if (inputMode === 'json' && jsonPreview.valid) {
          dataToSave.columns = jsonPreview.columns.join(',');
        }
        if (inputMode === 'simple') {
          // Clear JSON schema if using simple mode
          dataToSave.schema_json = '';
        }
        await onSave(dataToSave);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        // Validate it's valid JSON
        JSON.parse(content);
        handleInputChange('schema_json', content);
      } catch (err) {
        setErrors(prev => ({ ...prev, schema_json: 'Invalid JSON file' }));
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(formData.schema_json);
      handleInputChange('schema_json', JSON.stringify(parsed, null, 2));
    } catch (err) {
      // Ignore if invalid JSON
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 font-plex text-carbon-ink">
      <div className="bg-carbon-white border border-carbon-border max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-carbon-border bg-carbon-gray-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-carbon-white border border-carbon-border text-carbon-ink">
              <Table className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-carbon-ink m-0">
                {isEditing ? 'Edit Table Schema' : 'Add New Table Schema'}
              </h3>
              <p className="text-xs text-carbon-gray-70 m-0">
                Define the expected columns for automatic file matching
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Table Name */}
          <div>
            <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
              BigQuery Table Name <span className="text-carbon-red-60">*</span>
            </label>
            <p className="text-xs text-carbon-gray-50 mb-2">
              The exact table name in BigQuery (e.g., sales_data, inventory_report)
            </p>
            <input
              type="text"
              value={formData.table_name || ''}
              onChange={(e) => handleInputChange('table_name', e.target.value.toLowerCase())}
              placeholder="e.g., sales_data"
              disabled={saving || isEditing}
              className={`${INPUT} font-plex-mono ${
                errors.table_name ? 'border-carbon-red-60' : 'border-carbon-gray-30'
              } ${saving || isEditing ? 'bg-carbon-gray-10' : ''}`}
            />
            {errors.table_name && (
              <p className="text-xs text-carbon-red-60 mt-1">{errors.table_name}</p>
            )}
          </div>

          {/* Display Name */}
          <div>
            <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
              Display Name <span className="text-carbon-red-60">*</span>
            </label>
            <p className="text-xs text-carbon-gray-50 mb-2">
              Friendly name shown in the UI
            </p>
            <input
              type="text"
              value={formData.display_name || ''}
              onChange={(e) => handleInputChange('display_name', e.target.value)}
              placeholder="e.g., Sales Data Report"
              disabled={saving}
              className={`${INPUT} ${
                errors.display_name ? 'border-carbon-red-60' : 'border-carbon-gray-30'
              } ${saving ? 'bg-carbon-gray-10' : ''}`}
            />
            {errors.display_name && (
              <p className="text-xs text-carbon-red-60 mt-1">{errors.display_name}</p>
            )}
          </div>

          {/* Schema Input Mode Toggle */}
          <div>
            <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-2">
              Schema Definition Method <span className="text-carbon-red-60">*</span>
            </label>
            <div className="flex border border-carbon-gray-30 w-fit">
              <button
                type="button"
                onClick={() => setInputMode('simple')}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-medium ${
                  inputMode === 'simple'
                    ? 'bg-carbon-ink text-carbon-white'
                    : 'bg-carbon-white text-carbon-gray-70 hover:bg-carbon-gray-10'
                }`}
              >
                <Columns className="h-4 w-4" />
                Simple Columns
              </button>
              <button
                type="button"
                onClick={() => setInputMode('json')}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-medium border-l border-carbon-gray-30 ${
                  inputMode === 'json'
                    ? 'bg-carbon-ink text-carbon-white'
                    : 'bg-carbon-white text-carbon-gray-70 hover:bg-carbon-gray-10'
                }`}
              >
                <FileJson className="h-4 w-4" />
                JSON Schema
              </button>
            </div>
          </div>

          {/* Simple Columns Input */}
          {inputMode === 'simple' && (
            <div>
              <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
                Expected Columns <span className="text-carbon-red-60">*</span>
              </label>
              <p className="text-xs text-carbon-gray-50 mb-2">
                Comma-separated list of column headers (must match exactly in order). Example: date,product_id,quantity,amount
              </p>
              <textarea
                value={formData.columns || ''}
                onChange={(e) => handleInputChange('columns', e.target.value)}
                placeholder="date,product_id,quantity,amount"
                rows={3}
                disabled={saving}
                className={`${INPUT} font-plex-mono ${
                  errors.columns ? 'border-carbon-red-60' : 'border-carbon-gray-30'
                } ${saving ? 'bg-carbon-gray-10' : ''}`}
              />
              {errors.columns && (
                <p className="text-xs text-carbon-red-60 mt-1">{errors.columns}</p>
              )}
              {formData.columns && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {formData.columns.split(',').map((col, idx) => (
                    <span key={idx} className={`${CHIP} normal-case border-carbon-gray-30 text-carbon-gray-70`}>
                      {col.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* JSON Schema Input */}
          {inputMode === 'json' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">
                  BigQuery JSON Schema <span className="text-carbon-red-60">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={formatJson}
                    disabled={saving || !formData.schema_json}
                    className="text-xs font-medium text-carbon-blue-60 hover:text-carbon-blue-70 flex items-center gap-1"
                  >
                    <Code className="h-3 w-3" />
                    Format
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    className="text-xs font-medium text-carbon-blue-60 hover:text-carbon-blue-70 flex items-center gap-1"
                  >
                    <Upload className="h-3 w-3" />
                    Upload JSON
                  </button>
                </div>
              </div>
              <p className="text-xs text-carbon-gray-50 mb-2">
                Paste the BigQuery schema JSON exported from BigQuery Console or use the upload button
              </p>
              <textarea
                value={formData.schema_json || ''}
                onChange={(e) => handleInputChange('schema_json', e.target.value)}
                placeholder={`[\n  {\n    "name": "column_name",\n    "mode": "NULLABLE",\n    "type": "STRING",\n    "description": "",\n    "fields": []\n  }\n]`}
                rows={10}
                disabled={saving}
                className={`${INPUT} font-plex-mono text-xs ${
                  errors.schema_json ? 'border-carbon-red-60' : jsonPreview.valid ? 'border-carbon-green-50' : 'border-carbon-gray-30'
                } ${saving ? 'bg-carbon-gray-10' : ''}`}
              />
              {errors.schema_json && (
                <p className="text-xs text-carbon-red-60 mt-1">{errors.schema_json}</p>
              )}

              {/* JSON Preview */}
              {jsonPreview.valid && (
                <div className="relative mt-3 p-3 pl-4 bg-carbon-white border border-carbon-border">
                  <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-green-50" />
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-carbon-green-50" />
                    <span className="text-xs font-semibold text-carbon-ink">
                      Valid schema with {jsonPreview.columns.length} columns
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {jsonPreview.columns.map((col, idx) => (
                      <span key={idx} className={`${CHIP} normal-case border-carbon-green-50 text-carbon-green-50`}>
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {jsonPreview.error && formData.schema_json && (
                <div className="relative mt-3 p-3 pl-4 bg-carbon-white border border-carbon-border">
                  <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-red-60" />
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-carbon-red-60" />
                    <span className="text-xs text-carbon-ink">{jsonPreview.error}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bucket Path */}
          <div>
            <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
              Bucket Subfolder (Optional)
            </label>
            <p className="text-xs text-carbon-gray-50 mb-2">
              Optional subfolder within the bucket for organizing files
            </p>
            <input
              type="text"
              value={formData.bucket_path || ''}
              onChange={(e) => handleInputChange('bucket_path', e.target.value)}
              placeholder="e.g., sales/ or reports/monthly/"
              disabled={saving}
              className={`${INPUT} border-carbon-gray-30`}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe what this table contains..."
              rows={2}
              disabled={saving}
              className={`${INPUT} border-carbon-gray-30`}
            />
          </div>

          {/* Active */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.active !== false}
                onChange={(e) => handleInputChange('active', e.target.checked)}
                disabled={saving}
                className="h-4 w-4 text-carbon-blue-60 focus:ring-carbon-blue-60 border-carbon-gray-30"
              />
              <span className="text-sm text-carbon-gray-70">Active (allow file matching)</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-carbon-border">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={BTN_OUTLINE}
            >
              <X className="h-4 w-4 inline mr-1" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className={`${BTN_PRIMARY} flex items-center`}
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 inline mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 inline mr-1" />
              )}
              {saving ? 'Saving...' : (isEditing ? 'Update Schema' : 'Add Schema')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BIDataSettings() {
  const [config, setConfig] = useState({ bucket_name: '', dataset_name: 'inmocean_data' });
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSchemaForm, setShowSchemaForm] = useState(false);
  const [editingSchema, setEditingSchema] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [configData, schemasData] = await Promise.all([
        BIDataService.getConfig(),
        BIDataService.getTableSchemas()
      ]);
      setConfig(configData);
      setSchemas(Array.isArray(schemasData) ? schemasData : []);
    } catch (err) {
      console.error('Error loading BI data settings:', err);
      setError(`Failed to load settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    setError(null);
    try {
      await BIDataService.saveConfig(config);
      setSuccessMessage('Configuration saved successfully');
    } catch (err) {
      setError(`Failed to save configuration: ${err.message}`);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleAddSchema = () => {
    setEditingSchema(null);
    setShowSchemaForm(true);
  };

  const handleEditSchema = (schema) => {
    setEditingSchema(schema);
    setShowSchemaForm(true);
  };

  const handleSaveSchema = async (formData) => {
    setError(null);
    try {
      if (editingSchema) {
        const updated = await BIDataService.updateTableSchema(editingSchema.id, formData);
        setSchemas(prev => prev.map(item => item.id === updated.id ? { ...item, ...updated } : item));
        setSuccessMessage(`Schema "${formData.display_name}" updated successfully`);
      } else {
        const created = await BIDataService.addTableSchema(formData);
        setSchemas(prev => [created, ...prev]);
        setSuccessMessage(`Schema "${formData.display_name}" added successfully`);
      }
      setShowSchemaForm(false);
      setEditingSchema(null);
    } catch (err) {
      setError(`Failed to save schema: ${err.message}`);
    }
  };

  const handleDeleteSchema = async (schema) => {
    setError(null);
    try {
      await BIDataService.deleteTableSchema(schema.id);
      setSchemas(prev => prev.filter(item => item.id !== schema.id));
      setDeleteConfirm(null);
      setSuccessMessage(`Schema "${schema.display_name}" deleted successfully`);
    } catch (err) {
      setError(`Failed to delete schema: ${err.message}`);
    }
  };

  const filteredSchemas = schemas.filter(schema =>
    schema.table_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    schema.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    schema.columns?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="bg-carbon-white border border-carbon-border font-plex">
        <SkeletonRows rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-5 font-plex text-carbon-ink">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 flex items-center justify-center bg-carbon-gray-10 text-carbon-ink">
          <Database size={18} />
        </div>
        <div>
          <h1 className="text-lg font-semibold tracking-[-0.01em] m-0">BI Data Settings</h1>
          <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mt-0.5">
            Storage bucket · table schemas · automatic file matching
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="relative bg-carbon-white border border-carbon-border px-[18px] py-3 flex items-center gap-3">
          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-green-50" />
          <CheckCircle size={15} className="text-carbon-green-50 flex-shrink-0" />
          <span className="text-[13px] text-carbon-ink flex-1">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="text-carbon-gray-50 hover:text-carbon-ink flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="relative bg-carbon-white border border-carbon-border px-[18px] py-3 flex items-start gap-3">
          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-red-60" />
          <AlertCircle size={15} className="text-carbon-red-60 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-[13px] font-semibold text-carbon-ink">Error:</span>
            <p className="text-xs text-carbon-gray-70 m-0">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-carbon-gray-50 hover:text-carbon-ink flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Bucket Configuration */}
      <div className="bg-carbon-white border border-carbon-border">
        <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-carbon-ink" />
            <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Storage Configuration</span>
          </div>
          <p className="text-[11px] text-carbon-gray-50 mt-0.5 m-0">Configure the Google Cloud Storage bucket for file uploads</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
                GCS Bucket Name
              </label>
              <div className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-carbon-gray-40" />
                <input
                  type="text"
                  value={config.bucket_name || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, bucket_name: e.target.value }))}
                  placeholder="e.g., my-company-bi-data"
                  className={`${INPUT} flex-1 font-plex-mono border-carbon-gray-30`}
                />
              </div>
              <p className="text-xs text-carbon-gray-50 mt-1 m-0">The bucket where files will be uploaded</p>
            </div>
            <div>
              <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
                BigQuery Dataset
              </label>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-carbon-gray-40" />
                <input
                  type="text"
                  value={config.dataset_name || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, dataset_name: e.target.value }))}
                  placeholder="e.g., inmocean_data"
                  className={`${INPUT} flex-1 font-plex-mono border-carbon-gray-30`}
                />
              </div>
              <p className="text-xs text-carbon-gray-50 mt-1 m-0">The BigQuery dataset where tables will be created</p>
            </div>
            <div>
              <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
                Tenant Storage Path
              </label>
              <div className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-carbon-gray-40" />
                <input
                  type="text"
                  value={config.tenant_path || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, tenant_path: e.target.value }))}
                  placeholder="e.g., tenants/inmocean/bi-uploads"
                  className={`${INPUT} flex-1 font-plex-mono border-carbon-gray-30`}
                />
              </div>
              <p className="text-xs text-carbon-gray-50 mt-1 m-0">Folder path within the bucket for this tenant's uploads</p>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSaveConfig}
              disabled={savingConfig}
              className={`${BTN_PRIMARY} flex items-center gap-2`}
            >
              {savingConfig ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {savingConfig ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      </div>

      {/* Table Schemas */}
      <div className="bg-carbon-white border border-carbon-border">
        <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Columns className="h-4 w-4 text-carbon-ink" />
              <div>
                <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Table Schemas</span>
                <p className="text-[11px] text-carbon-gray-50 m-0">
                  Define expected columns for each table to enable automatic file matching
                </p>
              </div>
            </div>
            <button
              onClick={handleAddSchema}
              className={`${BTN_PRIMARY} flex items-center gap-2`}
            >
              <Plus className="h-4 w-4" />
              Add Schema
            </button>
          </div>
        </div>

        {/* Search — filter bar attached to the table top (Orders anatomy) */}
        <div className="px-[18px] py-2.5 border-b border-carbon-border flex items-center justify-between gap-4 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-carbon-gray-40 h-4 w-4" />
            <input
              type="text"
              placeholder="Search schemas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${INPUT} pl-10 border-carbon-gray-30`}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="font-plex-mono text-[10.5px] tracking-[0.08em] uppercase text-carbon-gray-50">
              Total: <span className="font-semibold text-carbon-ink">{filteredSchemas.length}</span>
            </span>
            <button
              onClick={loadData}
              className={`${BTN_OUTLINE} flex items-center gap-1.5`}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Schema Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className={`${TH} text-left`}>Table Name</th>
                <th className={`${TH} text-left`}>Display Name</th>
                <th className={`${TH} text-left`}>Schema Type</th>
                <th className={`${TH} text-left`}>Columns</th>
                <th className={`${TH} text-left`}>Status</th>
                <th className={`${TH} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSchemas.map((schema) => (
                <tr key={schema.id} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      <Table className="h-4 w-4 text-carbon-gray-50 mr-2" />
                      <span className="font-plex-mono text-[11.5px] font-medium text-carbon-ink">{schema.table_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[13px] text-carbon-ink">
                    {schema.display_name}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {schema.schema_json ? (
                      <span className={`${CHIP} border-carbon-blue-60 text-carbon-blue-60 inline-flex items-center gap-1`}>
                        <FileJson className="h-3 w-3" />
                        JSON
                      </span>
                    ) : (
                      <span className={`${CHIP} border-carbon-gray-30 text-carbon-gray-50 inline-flex items-center gap-1`}>
                        <Columns className="h-3 w-3" />
                        Simple
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {schema.columns?.split(',').slice(0, 5).map((col, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 font-plex-mono text-[10px] bg-carbon-gray-10 text-carbon-gray-70">
                          {col.trim()}
                        </span>
                      ))}
                      {schema.columns?.split(',').length > 5 && (
                        <span className="px-1.5 py-0.5 font-plex-mono text-[10px] text-carbon-gray-40">
                          +{schema.columns.split(',').length - 5} more
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`${CHIP} ${
                      schema.active !== false ? 'border-carbon-green-50 text-carbon-green-50' : 'border-carbon-gray-30 text-carbon-gray-50'
                    }`}>
                      {schema.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEditSchema(schema)}
                        className="text-carbon-gray-50 hover:text-carbon-blue-60 p-1 hover:bg-carbon-gray-10"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(schema)}
                        className="text-carbon-gray-50 hover:text-carbon-red-60 p-1 hover:bg-carbon-gray-10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State — canonical NoResults (echo the search) or the add-first CTA */}
        {filteredSchemas.length === 0 && (
          searchTerm ? (
            <NoResults query={searchTerm} onClear={() => setSearchTerm('')} />
          ) : (
            <div className="text-center py-12">
              <Columns className="mx-auto h-10 w-10 text-carbon-gray-40" />
              <h3 className="mt-2 text-sm font-semibold text-carbon-ink">No table schemas defined</h3>
              <p className="mt-1 text-xs text-carbon-gray-50">
                Add a schema to enable automatic file matching
              </p>
              <button
                onClick={handleAddSchema}
                className={`${BTN_PRIMARY} mt-4 inline-flex items-center gap-2`}
              >
                <Plus className="h-4 w-4" />
                Add First Schema
              </button>
            </div>
          )
        )}
      </div>

      {/* Info — module footnote */}
      <div className="flex gap-2 items-start text-carbon-gray-50 text-xs leading-relaxed max-w-[680px]">
        <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          How schema matching works: when you upload a file in the BI Data section, the system reads
          the column headers from the first row and compares them against all defined schemas. If a
          match is found, the file is automatically associated with that table and loaded into
          BigQuery with the correct table name.
        </span>
      </div>

      {/* Schema Form Modal */}
      {showSchemaForm && (
        <SchemaForm
          record={editingSchema}
          onSave={handleSaveSchema}
          onCancel={() => {
            setShowSchemaForm(false);
            setEditingSchema(null);
          }}
          isEditing={!!editingSchema}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 font-plex text-carbon-ink">
          <div className="bg-carbon-white border border-carbon-border max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center bg-carbon-gray-10 text-carbon-red-60">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <h3 className="text-[15px] font-semibold text-carbon-ink m-0">Delete Schema?</h3>
              </div>
              <p className="text-sm text-carbon-gray-70 mb-2">
                Are you sure you want to delete this table schema?
              </p>
              <p className="text-xs text-carbon-gray-50 mb-6">
                <strong>Table:</strong> <span className="font-plex-mono">{deleteConfirm.table_name}</span><br />
                <strong>Display Name:</strong> {deleteConfirm.display_name}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className={BTN_OUTLINE}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteSchema(deleteConfirm)}
                  className="px-4 py-2 text-xs font-medium text-carbon-white bg-carbon-red-60 hover:opacity-90"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
